import clsx from 'clsx';
import { lazy, memo, ReactNode, Suspense, useEffect } from 'react';
import { VirtualItem } from '@tanstack/react-virtual';
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableRowProps
} from '@material-ui/core';
import { useInView } from 'react-intersection-observer';
import { SPaginatedTableProps } from '../../paginated';
import { useVirtualTableStyles } from './style';
import TableHeader, {
  tableCheckboxStyle,
  TableHeaderProps
} from '../../components/table/header';
import { RowContainer } from '../../components/row-container';
import { BROWSERS, useBrowser } from '../../../../hooks';
import { SInfiniteScrollProps } from '../../../sinfinite-scroll';
import { SCheckbox, SCheckboxProps } from '../../../scheckbox';
import TableHeaderCell from '../../styles/TableHeaderCell';
import { SRadio } from '../../../sradio';

const LazyTableBodyCell = lazy(
  () => import('@setvi/shared/components/s-table/components/table/cells')
);

interface VirtualRow<T> extends Omit<TableRowProps, 'onClick'> {
  rowStyleGetter?: (row: T, index?: number) => React.CSSProperties;
  cellStyleGetter?: (row: T, index?: number) => React.CSSProperties;
  checkbox?: (row: T) => SCheckboxProps;
  onClick?: (row: T) => void;
  additionalContent?: (row: T) => JSX.Element;
  fallback?: ReactNode;
}

export interface SVirtualInfiniteScrollProps<T> extends Omit<
  SPaginatedTableProps<T>,
  'pagination' | 'showPagination' | 'customEmptyState' | 'tableStyle' | 'row'
> {
  id?: string;
  header?: Omit<Partial<TableHeaderProps>, 'checkboxFreeSpace'>;
  vList: VirtualItem[];
  height: number;
  infiniteScroll?: Omit<
    SInfiniteScrollProps<T>,
    | 'containerComponent'
    | 'wrapperComponent'
    | 'onWrapperClick'
    | 'list'
    | 'keyGetter'
    | 'render'
    | 'children'
  >;
  row?: VirtualRow<T>;
  options?: Record<string, string | number>;
  // Color even rows
  stripped?: boolean;
  // ! This is temp prop for distinguishing between the new and old design
  newDesign?: boolean;
  type?: 'none' | 'checkbox' | 'radio';
  showEmptyMessage?: boolean;
  footer?: JSX.Element;
}

const VirtualTable = <T,>({
  id,
  list,
  row = {},
  vList,
  type = 'checkbox',
  header,
  height,
  options,
  columns,
  isLoading,
  searchQuery,
  stickyHeader,
  infiniteScroll = {},
  stripped,
  footer,
  newDesign = false,
  showEmptyMessage = true,
  keyGetter
}: SVirtualInfiniteScrollProps<T>) => {
  const { browser } = useBrowser();
  const isSafari = browser === BROWSERS.safari;
  const {
    wrapperClass,
    containerClass,
    options: intersectionOptions,
    fetchMore,
    onWrapperDrop,
    onWrapperDrag,
    renderDragItem
  } = infiniteScroll;
  const {
    fallback,
    checkbox,
    onClick,
    additionalContent,
    rowStyleGetter,
    cellStyleGetter
  } = row;
  const classes = useVirtualTableStyles({
    wrapperClick: !!onClick,
    height,
    isSafari
  });
  const { ref, inView } = useInView(intersectionOptions);
  const checkboxFreeSpace = !header?.checkbox && !!row?.checkbox;

  useEffect(() => {
    if (inView) {
      fetchMore?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <Table stickyHeader={stickyHeader} className={classes.table}>
      <TableHeader
        newDesign={newDesign}
        columns={header?.columns || columns}
        checkboxFreeSpace={checkboxFreeSpace}
        rowStyle={{ display: 'flex' }}
        {...header}
      />

      <TableBody id={id} className={clsx(classes.tbody, containerClass)}>
        {vList.length === 0 && !isLoading && showEmptyMessage && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
            No data found
          </Box>
        )}

        {vList.map((virtualRow, index) => {
          const isLoaderRow = virtualRow.index > list?.length - 1;
          const element = list[virtualRow.index];

          if (!element) return null;

          return (
            <TableRow
              key={keyGetter(element)}
              onClick={() => onClick?.(element)}
              draggable={!!onWrapperDrag}
              onDragStart={() => onWrapperDrag?.(element)}
              className={clsx(classes.wrapper, wrapperClass)}
              onDragOver={e => e.preventDefault()}
              onDragStartCapture={e => {
                const div = renderDragItem?.(element);
                if (div) e.dataTransfer.setDragImage(div, 0, 0);
              }}
              onDrop={ev => {
                ev.preventDefault();
                ev.stopPropagation();
                onWrapperDrop?.(element);
              }}
              style={{
                ...rowStyleGetter?.(element, index),
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - ((options?.scrollMargin as number) || 0)}px) translateX(0px)`,

                ...(stripped
                  ? {
                      backgroundColor: virtualRow.index % 2 !== 0 && '#F8F9FA',
                      borderBottom: 'none',
                      borderRadius: 12
                    }
                  : {})
              }}
              ref={virtualRow?.index + 1 === list?.length ? ref : null}>
              {isLoaderRow ? (
                'Loading more...'
              ) : (
                <>
                  <Suspense fallback={fallback}>
                    <RowContainer
                      checkboxFreeSpace={checkboxFreeSpace}
                      additionalContent={additionalContent?.(element)}>
                      <>
                        {checkbox && (
                          <TableHeaderCell
                            style={{
                              ...cellStyleGetter?.(element, index),
                              ...tableCheckboxStyle
                            }}
                            onClick={(e: any) => e.stopPropagation()}>
                            {type === 'checkbox' ? (
                              <SCheckbox
                                onChange={e => {
                                  e.stopPropagation();
                                  checkbox(element).onChange?.(
                                    e,
                                    e?.target?.checked
                                  );
                                }}
                                checked={checkbox?.(element)?.checked}
                                disabled={checkbox?.(element)?.disabled}
                                tooltip={checkbox?.(element)?.tooltip}
                                {...checkbox}
                              />
                            ) : (
                              <SRadio
                                newDesign={newDesign}
                                name="radio-button-product"
                                onChange={e => {
                                  e.stopPropagation();
                                  checkbox(element).onChange?.(
                                    e,
                                    e?.target?.checked
                                  );
                                }}
                                checked={checkbox?.(element)?.checked}
                                disabled={checkbox?.(element)?.disabled}
                                tooltip={checkbox?.(element)?.tooltip}
                                {...checkbox}
                              />
                            )}
                          </TableHeaderCell>
                        )}
                        <LazyTableBodyCell
                          rowData={element}
                          columns={columns}
                          loading={isLoading}
                          searchQuery={searchQuery}
                          style={rowStyleGetter?.(element, index)}
                        />
                      </>
                    </RowContainer>
                  </Suspense>
                </>
              )}
            </TableRow>
          );
        })}

        {!!footer && footer}
      </TableBody>
    </Table>
  );
};

const SVirtualTable = memo(VirtualTable) as typeof VirtualTable;
export default SVirtualTable;
